/**
 * @module app.core
 */

import { Aurelia, autoinject } from 'aurelia-framework';
import { RouteConfig, Router } from 'aurelia-router';
import { I18N } from 'aurelia-i18n';
import { EventAggregator } from 'aurelia-event-aggregator';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { Api, AppSession } from 'app/shared/lib/essette-api';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { AccountContext } from 'app/core/lib/account-context';
import { State } from 'app/shared/lib/state';
import { ISignOnProvider } from 'app/core/models/essette-interfaces';

/**
 * A common foundation for pages that implement specific sign on flows
 */
@autoinject()
export abstract class SignOutBase {

    ////****************** Fields and Properties ************************************////
    protected readonly app!: Aurelia;
    protected router!: Router;
    protected readonly events!: EventAggregator;
    protected readonly api!: Api;
    protected readonly session!: AppSession;
    protected readonly logger!: AppLogger;
    protected readonly env!: EnvironmentService;
    protected readonly accountContext!: AccountContext;
    protected readonly i18n!: I18N;

    protected readonly busyState: State = new State();
    /** Whether External Sign Out is in process */
    protected signingOut: boolean = false;
    protected statusMessage: string | null = null;
    protected errorMessage: string | null = null;
    protected signOnProvider?: ISignOnProvider;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        app?: Aurelia,
        router?: Router,
        events?: EventAggregator,
        api?: Api,
        session?: AppSession,
        logger?: AppLogger,
        env?: EnvironmentService,
        accountContext?: AccountContext,
        i18n?: I18N,
    ) {
        this.app = app!;
        this.router = router!;
        this.events = events!;
        this.api = api!;
        this.session = session!;
        this.logger = logger!;
        this.env = env!;
        this.accountContext = accountContext!;
        this.i18n = i18n!;
        this.logger.source = this;
    }

    protected async activate(params: any, routeConfig: RouteConfig): Promise<void> {
        try {
            this.busyState.beginLoad();

            const { provider } = params;
            if(provider !== undefined) {
                // strip the param and swap the URL
                params.provider = null;
                this.router.navigateToRoute(routeConfig.name!, params, { trigger: false, replace: true });
            }
            else {
                throw new Error("Missing sign on provider parameter");
            }

            this.signingOut = true;

            this.loadSignOnProvider(provider);
            if(!this.signOnProvider) {
                throw new Error("Unknown Sign On provider");
            }
        }
        catch(error) {
            this.logger.error(error);
            this.errorMessage = this.i18n.tr('core:local.signon.unknownProvider');
            this.signingOut = false;
        }
        finally {
            this.busyState.endLoad();
        }
    }

    ////****************** Functions ************************************************////
    /**
     * Load a Sign On Provider
     */
    protected loadSignOnProvider(key: string): void {
        const signOnProviders = this.env.config.signOnProviders;
        this.signOnProvider = signOnProviders?.find(p => p.key === key);
    }

    /**
     * Initiate the external Sign Out process for a given Sign On Provider
     */
    protected abstract signOut(): Promise<void>;

    /**
     * Log the user out of this application
     */
    protected async localLogOut(): Promise<void> {
        if(this.session.isLoggedIn()) {
            this.api.logOut();
            this.accountContext.clear();
            this.session.clear('logOut');
        }
    }
}
